import React, { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { usePatchChangeShipping } from 'services/Data/CartService';
import CustomInput from '@acnb/CustomComponents/CustomInput/CustomInput';
import SkeletonWrapper from 'Components/SkeletonWrapper';
import GridContainer from '@jumbo/components/GridContainer';
import GridItem from '@jumbo/components/GridItem';
import { validateEmail } from 'services/Utilities/Utilities';
import PhoneInput from '@acnb/CustomComponents/PhoneInput/PhoneInput';

const splitPrincipalContactName = principalContact => {
  if (!principalContact?.trim()) {
    return { firstName: '', lastName: '' };
  }

  const [firstName, ...lastNameParts] = principalContact.trim().split(/\s+/);
  return {
    firstName: firstName || '',
    lastName: lastNameParts.join(' '),
  };
};

const ContactInfoCheckoutSection = ({ cart, isLoading, validator, isDisabled, isClubMode = false, clubDealer = null }) => {
  const { t } = useTranslation();

  const { mutate: changeShipping } = usePatchChangeShipping();

  const [phoneValue, setPhoneValue] = useState(cart?.phone ?? '');
  const [isPhoneFocused, setIsPhoneFocused] = useState(false);

  const [email, setEmail] = useState(cart?.email ?? '');

  useEffect(() => {
    setPhoneValue(cart?.phone ?? '');
  }, [cart?.phone]);

  useEffect(() => {
    setEmail(cart?.email ?? '');
  }, [cart?.email]);

  useEffect(() => {
    if (!isClubMode || !cart || !clubDealer) {
      return;
    }

    const principalContact = clubDealer.principalContact?.trim() || '';
    const principalContactEmail = clubDealer.principalContactEmail?.trim() || '';

    if (!principalContact || !principalContactEmail) {
      return;
    }

    const currentFirstName = cart.firstName || '';
    const currentLastName = cart.lastName || '';
    const currentEmail = cart.email || '';

    if (currentFirstName && currentLastName && currentEmail) {
      return;
    }

    const { firstName, lastName } = splitPrincipalContactName(principalContact);
    const nextFirstName = firstName || cart.firstName || '';
    const nextLastName = lastName || cart.lastName || firstName || '';

    const shouldSyncContactInfo =
      (cart.firstName || '') !== nextFirstName ||
      (cart.lastName || '') !== nextLastName ||
      (cart.email || '') !== principalContactEmail;

    if (!shouldSyncContactInfo) {
      return;
    }

    setEmail(principalContactEmail);
    changeShipping({
      ...cart,
      firstName: nextFirstName,
      lastName: nextLastName,
      email: principalContactEmail,
    });
  }, [cart, changeShipping, clubDealer, isClubMode]);

  return (
    <SkeletonWrapper
      isLoading={isLoading}
      component={
        <GridContainer>
          <GridItem xs={12} sm={6}>
            <CustomInput
              labelText={t('firstName')}
              id="firstName"
              formControlProps={{
                fullWidth: true,
              }}
              error={validator.current.message(t('firstName'), cart?.firstName, `required`)}
              inputProps={{
                onBlur: ({ target: { value } }) => {
                  changeShipping({ ...cart, firstName: value });
                },
                disabled: !cart || isDisabled,
                defaultValue: cart?.firstName ?? '',
              }}
            />
          </GridItem>

          <GridItem xs={12} sm={6}>
            <CustomInput
              labelText={t('lastName')}
              id="lastName"
              formControlProps={{
                fullWidth: true,
              }}
              error={validator.current.message(t('lastName'), cart?.lastName, `required`)}
              inputProps={{
                onBlur: ({ target: { value } }) => {
                  changeShipping({ ...cart, lastName: value });
                },
                disabled: !cart || isDisabled,
                defaultValue: cart?.lastName ?? '',
              }}
            />
          </GridItem>

          <GridItem xs={12}>
            <CustomInput
              labelText={t('email')}
              id="email"
              formControlProps={{
                fullWidth: true,
              }}
              error={validator.current.message(t('email'), email, 'required|email')}
              inputProps={{
                type: 'email',
                value: email,
                onChange: ({ target: { value } }) => {
                  setEmail(value);
                },
                onBlur: () => {
                  if (validateEmail(email)) {
                    changeShipping({ ...cart, email });
                  }

                  validator.current.showMessageFor(t('email'));
                },
                disabled: !cart || isDisabled,
              }}
            />
          </GridItem>

          <GridItem xs={12}>
            <CustomInput
              labelText={t('phone')}
              id="phone"
              labelProps={{ shrink: isPhoneFocused || !!phoneValue || (isDisabled && !!cart?.phone) }}
              formControlProps={{
                fullWidth: true,
              }}
              error={validator.current.message(t('phone'), cart?.phone, `required`)}
              inputProps={{
                inputComponent: PhoneInput,
                value: phoneValue,
                onChange: e => setPhoneValue(e.target.value),
                onFocus: () => setIsPhoneFocused(true),
                onBlur: ({ target: { value } }) => {
                  setIsPhoneFocused(false);
                  changeShipping({ ...cart, phone: value });
                },
                disabled: !cart || isDisabled,
              }}
            />
          </GridItem>
        </GridContainer>
      }
    />
  );
};

export default ContactInfoCheckoutSection;
